import React, { useState, useEffect } from "react";
import { FaRegEdit } from "react-icons/fa";
import { IoMdArrowDropright, IoMdArrowDropup } from "react-icons/io";
import { getMerchantIPs, getUser } from "../../../config/LiveApi";
import { useSelector } from "react-redux";
import UpdateModal from "./UpdateModal";
import AddNewIp from "./AddNewIp";
import "./Ips.css";

const IpsTable = () => {
  const role = useSelector((store) => store.userReducer.role);
  const token = useSelector((store) => store.userReducer.token);
  const [isLoading, setIsLoading] = useState(true);
  const [merchantApikeys, setMerchantApikeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [expandedMerchants, setExpandedMerchants] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIpData, setSelectedIpData] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const isAdminRole = role === "admin" || role === "subAdmin";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const merchantPromise = getMerchantIPs(token);
        const userPromise = isAdminRole ? null : getUser(token);
        const [merchantData, userData] = await Promise.all([merchantPromise, userPromise]);
        setMerchantApikeys(merchantData);
        if (userData) setUsers(userData.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token, role]);

  const getUserNameById = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user ? user.alias || user.id : "N/A";
  };

  const handleExpandToggle = (merchantId) => {
    setExpandedMerchants((prev) => ({ ...prev, [merchantId]: !prev[merchantId] }));
  };

  const handleEditClick = (ip) => {
    setSelectedIpData(ip);
    setIsModalOpen(true);
  };

  const handleUpdateStatus = (ipId, newStatus) => {
    setMerchantApikeys((prev) =>
      prev.map((ip) => (ip.id === ipId ? { ...ip, status: newStatus } : ip))
    );
  };

  const groupIpsByMerchant = () => {
    return merchantApikeys.reduce((acc, api) => {
      if (!acc[api.userId]) acc[api.userId] = [];
      acc[api.userId].push(api);
      return acc;
    }, {});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  return (
    <div className="ips-container">
      <div className="ips-header">
        <div className="ips-header-left">
          <h2 className="ips-title">Merchant IPs</h2>
          <span className="ips-count">{merchantApikeys.length} total</span>
        </div>
        <button className="ips-modal-buttons" onClick={() => setIsAddModalOpen(true)}>
          + Add New IP
        </button>
      </div>

      <div className="ips-table-wrapper">
        <table className="ips-table">
          <thead>
            <tr className="ips-table-head-row">
              {!isAdminRole && <th className="ips-th">Merchant</th>}
              <th className="ips-th">IP Address</th>
              <th className="ips-th">Status</th>
              <th className="ips-th">Created</th>
              <th className="ips-th">Updated</th>
              {role === 'super' && <th className="ips-th ips-th-center">Actions</th>}
              {role === 'super' && <th className="ips-th ips-th-center">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="6" className="ips-empty-cell">
                  <div className="ips-loading">
                    <span className="ips-loading-dot" />
                    <span className="ips-loading-dot" />
                    <span className="ips-loading-dot" />
                  </div>
                </td>
              </tr>
            ) : merchantApikeys.length === 0 ? (
              <tr>
                <td colSpan="6" className="ips-empty-cell">No data available</td>
              </tr>
            ) : isAdminRole ? (
              merchantApikeys.map((ip) => (
                <tr key={ip.id} className="ips-table-row">
                  <td className="ips-td ips-td-mono">{ip.ip}</td>
                  <td className="ips-td">
                    <span className={`ips-status-badge ${ip.status ? "ips-status-active" : "ips-status-inactive"}`}>
                      <span className="ips-status-dot" />
                      {ip.status ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="ips-td ips-td-muted">{formatDate(ip.createdAt)}</td>
                  <td className="ips-td ips-td-muted">{formatDate(ip.updatedAt)}</td>
                </tr>
              ))
            ) : (
              Object.keys(groupIpsByMerchant()).map((merchantId) => {
                const ips = groupIpsByMerchant()[merchantId];
                const isExpanded = expandedMerchants[merchantId] || false;
                const merchantName = getUserNameById(merchantId);

                return (
                  <React.Fragment key={merchantId}>
                    {/* Merchant header row */}
                    <tr
                      className="ips-merchant-row"
                      onClick={() => handleExpandToggle(merchantId)}
                    >
                      <td className="ips-td ips-td-merchant">
                        <div className="ips-merchant-name-cell">
                          <span className="ips-chevron">
                            {isExpanded ? <IoMdArrowDropup size={20} /> : <IoMdArrowDropright size={20} />}
                          </span>
                          <span className="ips-merchant-name">{merchantName}</span>
                          <span className="ips-merchant-ip-count">{ips.length} IP{ips.length !== 1 ? "s" : ""}</span>
                        </div>
                      </td>
                      <td className="ips-td ips-td-mono">{ips[0].ip}</td>
                      <td className="ips-td">
                        <span className={`ips-status-badge ${ips[0].status ? "ips-status-active" : "ips-status-inactive"}`}>
                          <span className="ips-status-dot" />
                          {ips[0].status ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="ips-td ips-td-muted">{formatDate(ips[0].createdAt)}</td>
                      <td className="ips-td ips-td-muted">{formatDate(ips[0].updatedAt)}</td>
                      {role === 'super' &&
                        <td className="ips-td ips-td-center">
                        <button
                          className="ips-edit-btn"
                          onClick={(e) => { e.stopPropagation(); handleEditClick(ips[0]); }}
                          >
                          <FaRegEdit size={15} />
                        </button>
                      </td>
                        }
                    </tr>

                    {/* Expanded IP rows */}
                    {isExpanded && ips.slice(1).map((ip, idx) => (
                      <tr key={ip.id} className={`ips-ip-row ${idx === ips.length - 2 ? "ips-ip-row-last" : ""}`}>
                        <td className="ips-td ips-td-indent">
                          <div className="ips-indent-line" />
                        </td>
                        <td className="ips-td ips-td-mono">{ip.ip}</td>
                        <td className="ips-td">
                          <span className={`ips-status-badge ${ip.status ? "ips-status-active" : "ips-status-inactive"}`}>
                            <span className="ips-status-dot" />
                            {ip.status ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="ips-td ips-td-muted">{formatDate(ip.createdAt)}</td>
                        <td className="ips-td ips-td-muted">{formatDate(ip.updatedAt)}</td>
                       { role === 'super' && <td className="ips-td ips-td-center">
                          <button
                            className="ips-edit-btn"
                            onClick={() => handleEditClick(ip)}
                          >
                            <FaRegEdit size={15} />
                          </button>
                        </td>}
                      </tr>
                      
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <UpdateModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          ipData={selectedIpData}
          token={token}
          onUpdate={handleUpdateStatus}
        />
      )}
      {isAddModalOpen && <AddNewIp onClose={() => setIsAddModalOpen(false)} />}
    </div>
  );
};

export default IpsTable;